import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckboxList } from '@/components/core/checkbox-list/checkbox-list';
import { SearchableMultiSelect } from '@/components/core/searchable-multi-select/searchable-multi-select';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui-kit/form';
import { Label } from '@/components/ui-kit/label';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useAssignableRoles } from '@/hooks/use-assignable-roles';
import { isDepartmentRole } from '@/lib/department-scope';
import { isRoleOfOrganization } from '@/lib/organization-roles';
import { getDepartmentKeysByOrganization } from '@/modules/org-setup/hr-department-manager/services/department.service';
import {
  OrganizationOption,
  useOrganizationNamesWithStatus,
  useOrganizations,
} from '@/hooks/use-organizations';
import { OrganizationRoleAssignment } from '../../../types/employee.types';

/** The role dialog's form: every organization the person has access to and the roles held in each. */
export interface RoleFormValues {
  OrganizationAccess: OrganizationRoleAssignment[];
}

interface Option {
  value: string;
  label: string;
}

/**
 * Organizations (Blocks OS IAM organizations) and, for each one, the roles the employee holds there.
 *
 * An admin can only grant organizations they themselves belong to — the organization picker is built from
 * the signed-in admin's own access (`useOrganizations`), never a tenant-wide browse. Within those, any
 * assignable role can be granted. Editing an employee who already holds an organization or role outside
 * that must still show it rather than silently drop it, so those are merged in as extra options.
 */
export const RoleInfoStep = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<RoleFormValues>();
  const { data: assignableRoles = [], isLoading: isLoadingRoles } = useAssignableRoles();
  const { data: myOrganizations = [], isLoading: isLoadingOrganizations } = useOrganizations();

  const organizationAccess = useWatch({ control, name: 'OrganizationAccess' });

  const roleNameBySlug = useMemo(
    () => new Map(assignableRoles.map((role) => [role.slug, role.name])),
    [assignableRoles]
  );

  // Every department's Head of Department role exists in every organization (IAM roles are
  // project-wide), but it only means something in the department's own organization.
  const organizationIds = useMemo(
    () =>
      [...new Set(organizationAccess.map((organization) => organization.OrganizationId))].sort(),
    [organizationAccess]
  );
  const { data: departmentKeysByOrganization = {}, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['hr-department-keys-by-organization', organizationIds],
    queryFn: () => getDepartmentKeysByOrganization(organizationIds),
    enabled: organizationIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Organizations already assigned (editing an existing employee) that the signed-in admin doesn't
  // themselves hold — still shown, with a best-effort resolved name, so editing never silently drops them.
  const extraOrganizationIds = useMemo(
    () =>
      [...new Set(organizationAccess.map((organization) => organization.OrganizationId))].filter(
        (id) => !myOrganizations.some((organization) => organization.itemId === id)
      ),
    [organizationAccess, myOrganizations]
  );
  const { names: extraOrganizationNames, isLoading: isLoadingExtraNames } =
    useOrganizationNamesWithStatus(extraOrganizationIds);
  const allOrganizations: OrganizationOption[] = [
    ...myOrganizations,
    ...extraOrganizationIds.map((itemId) => ({
      itemId,
      name: extraOrganizationNames[itemId] ?? itemId,
      roles: [],
    })),
  ];

  const organizationOptions: Option[] = allOrganizations.map((organization) => ({
    value: organization.itemId,
    label: organization.name,
  }));
  const organizationName = (organizationId: string) =>
    allOrganizations.find((organization) => organization.itemId === organizationId)?.name ??
    organizationId;

  /**
   * The roles selectable for one organization: every role IAM lets the signed-in admin assign (holding
   * `DIRECTORY.ASSIGN_ROLE` is what opens this dialog) — of the organization roles only this
   * organization's own, of the Head of Department roles only those of its departments — plus any
   * already granted so editing never drops them.
   */
  const roleOptionsFor = (organizationId: string, alreadyGranted: string[]): Option[] => {
    const ownDepartments = new Set(departmentKeysByOrganization[organizationId] ?? []);
    const name = organizationName(organizationId);
    const assignable = assignableRoles
      .map((role) => role.slug)
      .filter((slug) =>
        isDepartmentRole(slug)
          ? ownDepartments.has(slug)
          : isRoleOfOrganization(slug, name, organizationId)
      );
    const slugs = [...new Set([...assignable, ...alreadyGranted])];
    return slugs.map((slug) => ({ value: slug, label: roleNameBySlug.get(slug) ?? slug }));
  };

  const handleOrganizationChange = (nextIds: string[]) => {
    const next = nextIds.map(
      (organizationId) =>
        organizationAccess.find(
          (organization) => organization.OrganizationId === organizationId
        ) ?? {
          OrganizationId: organizationId,
          RoleKeys: [],
        }
    );
    setValue('OrganizationAccess', next, { shouldValidate: true });
  };

  const updateOrganizationRoles = (organizationId: string, roleKeys: string[]) => {
    setValue(
      'OrganizationAccess',
      organizationAccess.map((organization) =>
        organization.OrganizationId === organizationId
          ? { ...organization, RoleKeys: roleKeys }
          : organization
      ),
      { shouldValidate: true }
    );
  };

  return (
    <div className="space-y-6">
      <FormField
        control={control}
        name="OrganizationAccess"
        render={() => (
          <FormItem>
            <FormLabel>{t('ORGANIZATIONS')}</FormLabel>
            <FormControl>
              {isLoadingOrganizations || isLoadingExtraNames ? (
                <Skeleton className="h-9 w-full rounded-md" />
              ) : (
                <SearchableMultiSelect
                  options={organizationOptions}
                  value={organizationAccess.map((organization) => organization.OrganizationId)}
                  onChange={handleOrganizationChange}
                  placeholder={t('SELECT_ORGANIZATIONS')}
                  searchPlaceholder={t('SEARCH')}
                  emptyText={t('NO_RESULTS_FOUND')}
                  selectedText={t('SELECTED')}
                  removeLabel={(label) => `${t('REMOVE')} ${label}`}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {organizationAccess.length > 0 && (
        <div className="space-y-2">
          <Label>{t('ORGANIZATION_WISE_ROLES')}</Label>
          <div className="space-y-3">
            {organizationAccess.map((organization, index) => {
              const roleOptions = roleOptionsFor(
                organization.OrganizationId,
                organization.RoleKeys
              );
              return (
                <div key={organization.OrganizationId} className="rounded-md border p-3">
                  {/* Not the organization's raw id while its name is loading. */}
                  {isLoadingExtraNames ? (
                    <Skeleton className="mb-3 h-5 w-40" />
                  ) : (
                    <p className="mb-3 text-sm font-medium">
                      {organizationName(organization.OrganizationId)}
                    </p>
                  )}
                  {isLoadingRoles || isLoadingDepartments ? (
                    <Skeleton className="h-24 w-full rounded-md" />
                  ) : roleOptions.length === 0 ? (
                    <div className="flex h-24 items-center justify-center rounded-md border p-3 text-center text-sm text-muted-foreground">
                      {t('NO_ASSIGNABLE_ROLES_FOUND')}
                    </div>
                  ) : (
                    <CheckboxList
                      options={roleOptions}
                      value={organization.RoleKeys}
                      onChange={(roleKeys) =>
                        updateOrganizationRoles(organization.OrganizationId, roleKeys)
                      }
                      className="max-h-none overflow-visible"
                    />
                  )}
                  {/* Shown here, not on the picker: an organization left without a role blocks Save. */}
                  <FormField
                    control={control}
                    name={`OrganizationAccess.${index}.RoleKeys`}
                    render={() => (
                      <FormItem className="mt-2">
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
